/**
 * SmoothScrollProvider — global Lenis smooth-scroll wrapper.
 *
 * - Initialises a single Lenis instance on mount.
 * - Disabled automatically when `prefers-reduced-motion: reduce` is set.
 * - Disabled inside any element with `data-lenis-prevent` so admin pages
 *   and modal scroll lists keep their native behaviour.
 */
import { useEffect } from "react";
import Lenis from "lenis";
import usePreferStaticUI from "@/lib/usePreferStaticUI";

import { useLocation } from "react-router-dom";

export const SmoothScrollProvider = ({ children }) => {
  const reduced = usePreferStaticUI();
  const location = useLocation();

  useEffect(() => {
    // Don't run on admin routes — keep them snappy
    if (location.pathname.startsWith("/admin")) return;
    // Portal also gets native scroll for predictable mobile feel
    if (location.pathname.startsWith("/portal")) return;
    if (reduced) return;

    const lenis = new Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      wheelMultiplier: 1.0,
      smoothWheel: true,
    });

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [reduced, location.pathname]);

  return children;
};

export default SmoothScrollProvider;
